import type { FormEvent } from 'react';
import React from 'react';
import { range } from 'lodash-es';

import {
  Flex,
  FormField,
  FormHelp,
  IconPlusOutline,
  Input,
  Label,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation';

import type { Section } from '../../../models/index';
import { Field, PaymentMethodSection } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants';
import PaymentMethodListBox from './PaymentMethod/PaymentMethodListBox';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_PAYMENT_METHODS = 5;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PaymentMethodSection) => void;
};

export function PaymentMethod_v2({ section, setSectionValues, setSection }: Props): JSX.Element {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: PaymentMethodSection,
  });

  const errorMsg = section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)
    ? PAYMENT_METHOD.ERROR_MESSAGE.ADDRESS
    : null;

  function onRemoveAll(): void {
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function onAddressChange(event: FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS]: value,
    });
  }

  function setName(e: FormEvent<HTMLInputElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(
      PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME,
      Number(index),
    );

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderIndividualPaymentMethod(index: number) {
    const nameField = section.getField(
      Field.getIndexedFieldName(PAYMENT_METHOD.FIELD_NAME.INDIVIDUAL_ELECTRONIC_PAYMENT_NAME, index),
    );

    return (
      <FormField
        marginTop="sm"
        label={
          <Flex display="flex" justifyContent="space-between" alignItems="center" marginBottom="xs">
            <Label id={`electronic-payment-method-#${index + 1}`}>Service #{index + 1}</Label>
            {index > 0 && (
              <TextButton
                data-index={index}
                data-test="delete-electronic-payment-method"
                onClick={onDeleteOne}
                fontType="bodySmallHeading"
              >
                Delete
              </TextButton>
            )}
          </Flex>
        }
        control={
          <Input
            aria-labelledby={`electronic-payment-method-#${index + 1}`}
            data-index={index}
            data-test="electronic-payment-method-name"
            value={nameField.enteredValue}
            onChange={setName}
          />
        }
      />
    );
  }

  const totalPaymentMethods = section.getTotalIndexes();
  if (totalPaymentMethods == null) {
    return null;
  }

  return (
    <>
      <PaymentMethodListBox
        section={section}
        setSectionValues={setSectionValues}
        onAddOne={onAddOne}
        onRemoveAll={onRemoveAll}
      />
      {section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED) && (
        <FormField
          marginTop="sm"
          label={<Label>To whom should the tenant send check or money order?</Label>}
          control={
            <Textarea
              data-test="tenant-send-check"
              error={errorMsg}
              onChange={onAddressChange}
              value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
              resize="vertical"
              rows="3"
            />
          }
          description={<FormHelp>Recipient’s name, street, city, state, and ZIP Code</FormHelp>}
        />
      )}
      {range(0, totalPaymentMethods).map((index: number) => (
        <React.Fragment key={index}>{renderIndividualPaymentMethod(index)}</React.Fragment>
      ))}
      {totalPaymentMethods > 0 && (
        <Flex display="flex" justifyContent="center" alignItems="center" marginTop="sm">
          {totalPaymentMethods < MAXIMUM_PAYMENT_METHODS ? (
            totalPaymentMethods >= 0 && (
              <TextButton icon={<IconPlusOutline />} onClick={onAddOne} data-test="add-another">
                Add another electronic payment service
              </TextButton>
            )
          ) : (
            <Flex display="flex" justifyContent="center" alignItems="center">
              <Text data-test="maximum-reached">
                You’ve added {MAXIMUM_PAYMENT_METHODS} electronic payment services, which is the maximum allowed.
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
}

export default PaymentMethod_v2;
